import { useState } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function HexRgbConverter() {
  const [hex, setHex] = useState("#3B82F6");
  const [rgb, setRgb] = useState("rgb(59, 130, 246)");
  const [copiedHex, setCopiedHex] = useState(false);
  const [copiedRgb, setCopiedRgb] = useState(false);

  const hexToRgb = (h: string) => {
    let r = 0, g = 0, b = 0;
    // 3 digits
    if (h.length === 4) {
      r = parseInt("0x" + h[1] + h[1]);
      g = parseInt("0x" + h[2] + h[2]);
      b = parseInt("0x" + h[3] + h[3]);
    } 
    // 6 digits
    else if (h.length === 7) {
      r = parseInt("0x" + h[1] + h[2]);
      g = parseInt("0x" + h[3] + h[4]);
      b = parseInt("0x" + h[5] + h[6]);
    } else {
      return null;
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const rgbToHex = (r: string) => {
    const sep = r.indexOf(",") > -1 ? "," : " ";
    const arr = r.substr(4).split(")")[0].split(sep);
    
    if (arr.length !== 3) return null;
    
    let red = Number(arr[0].trim()).toString(16);
    let green = Number(arr[1].trim()).toString(16);
    let blue = Number(arr[2].trim()).toString(16);

    if (red.length === 1) red = "0" + red;
    if (green.length === 1) green = "0" + green;
    if (blue.length === 1) blue = "0" + blue;

    return "#" + red + green + blue;
  };

  const handleHexChange = (val: string) => {
    setHex(val);
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(val)) {
      const newRgb = hexToRgb(val);
      if (newRgb) setRgb(newRgb);
    }
  };

  const handleRgbChange = (val: string) => {
    setRgb(val);
    if (val.startsWith("rgb(") && val.endsWith(")")) {
      const newHex = rgbToHex(val);
      if (newHex) setHex(newHex);
    }
  };

  return (
    <ToolPageLayout
      title="Hex to RGB Converter – Online Color Format Tool"
      description="Convert Hex color codes to RGB and back instantly with our free online color converter tool. Perfect for web designers and developers."
      h1="Hex to RGB Converter"
      category="Color Tools"
      categoryPath="/color-tools"
      howToUse={[
        "Enter your Hex code (e.g., #3B82F6) to get the RGB value.",
        "Enter your RGB code (e.g., rgb(59, 130, 246)) to get the Hex value.",
        "Copy the results using the copy icon."
      ]}
      faq={[
        { question: "How to convert hex to RGB?", answer: "Simply input your hex code in the tool, and the RGB value will be calculated automatically." },
        { question: "What is the difference between HEX and RGB?", answer: "HEX is a 6-digit code for colors, while RGB defines colors using Red, Green, and Blue light values." },
        { question: "How to get RGB value from hex code?", answer: "Use our Hex to RGB converter tool; it instantly converts color formats." }
      ]}
    >
      <div className="max-w-xl mx-auto space-y-8">
        <div 
          className="w-full h-48 rounded-xl shadow-inner border border-slate-200 transition-colors"
          style={{ backgroundColor: /^#([A-Fa-f0-9]{3}){1,2}$/.test(hex) ? hex : "#ffffff" }}
        />
        
        <div className="grid gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">HEX Color</label>
            <div className="flex gap-2">
              <Input 
                value={hex} 
                onChange={(e) => handleHexChange(e.target.value)} 
                placeholder="#000000"
                className="font-mono text-lg h-12 uppercase"
              />
              <Button
                variant="outline"
                className="h-12 w-12 shrink-0"
                aria-label="Copy Hex value to clipboard"
                onClick={() => {
                  copyToClipboard(hex);
                  setCopiedHex(true);
                  setTimeout(() => setCopiedHex(false), 2000);
                }}
              >
                <Copy className="h-5 w-5" />
              </Button>
            </div>
            {copiedHex && <span className="text-xs text-green-600">Copied to clipboard!</span>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">RGB Color</label>
            <div className="flex gap-2">
              <Input 
                value={rgb} 
                onChange={(e) => handleRgbChange(e.target.value)} 
                placeholder="rgb(0, 0, 0)"
                className="font-mono text-lg h-12 lowercase"
              />
              <Button
                variant="outline"
                className="h-12 w-12 shrink-0"
                aria-label="Copy RGB value to clipboard"
                onClick={() => {
                  copyToClipboard(rgb);
                  setCopiedRgb(true);
                  setTimeout(() => setCopiedRgb(false), 2000);
                }}
              >
                <Copy className="h-5 w-5" />
              </Button>
            </div>
            {copiedRgb && <span className="text-xs text-green-600">Copied to clipboard!</span>}
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
